import { Button, Plus, useSnackbar } from 'component-library';
import { useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type NewContainer } from '@sr/types';
import { useArticleGroups } from '#lib/hooks/useArticleGroups';
import { CustomerPortalApiError } from '../../customerPortalApi/customerPortalApiError';
import { ContainerRow } from './ContainerRow';
import {
  createEmptyContainer,
  isEmptyContainer,
  validatedContainerFields,
} from './containerFields';
import { ContainerRowsWrapper } from './styles';

type ContainerRowsForm = {
  containers?: NewContainer[];
};

export const ContainerRows = ({
  locationDwKey,
  useGrandParentLocation,
}: {
  locationDwKey: string;
  useGrandParentLocation?: boolean;
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { control, setValue } = useFormContext<ContainerRowsForm>();
  const { errors } = useFormState({ control, name: 'containers' });
  const containers = useWatch({ control, name: 'containers' }) ?? [];

  const [country, bpid] = locationDwKey.split('_');
  const businessPartnerDwKey = `${country}_${bpid}`;

  const { articles, isPending, error } = useArticleGroups({
    selectedBusinessPartners: [businessPartnerDwKey],
    useGrandParentLocation,
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    const isTimeout =
      error instanceof CustomerPortalApiError && (error.status === 408 || error.status === 504);

    showSnackbar({
      severity: 'error',
      description: isTimeout
        ? t('add-container.material-picker.components.message.error.timeout.text')
        : t('message.components.message.error.default.text'),
    });
  }, [error, showSnackbar, t]);

  // Revalidate only after a failed step change, so errors clear as fields get filled in.
  const updateContainers = (updatedContainers: NewContainer[]) => {
    setValue('containers', updatedContainers, { shouldValidate: !!errors.containers });
  };

  const removeContainer = (id: string) => {
    const remainingContainers = containers.filter((container) => container.id !== id);

    updateContainers(
      remainingContainers.length > 0 ? remainingContainers : [createEmptyContainer()],
    );
  };

  const getRowErrors = (index: number) => {
    const rowErrors = errors.containers?.[index];

    return Object.fromEntries(
      validatedContainerFields.map((field) => {
        const message = rowErrors?.[field]?.message;

        return [field, message ? t(message) : undefined];
      }),
    );
  };

  return (
    <ContainerRowsWrapper id="containers">
      {containers.map((container, index) => (
        <ContainerRow
          key={container.id}
          container={container}
          errors={getRowErrors(index)}
          businessPartnerDwKey={businessPartnerDwKey}
          useGrandParentLocation={useGrandParentLocation}
          articles={articles}
          isArticlesPending={isPending}
          removeDisabled={containers.length === 1 && isEmptyContainer(container)}
          onChange={(changes) => {
            updateContainers(
              containers.map((current) =>
                current.id === container.id ? { ...current, ...changes } : current,
              ),
            );
          }}
          onRemove={() => {
            removeContainer(container.id);
          }}
        />
      ))}
      <Button
        variant="text-primary"
        iconPlacement="before"
        icon={<Plus />}
        onClick={() => {
          updateContainers([...containers, createEmptyContainer()]);
        }}
      >
        {t('rented-containers.add-containers.step1.add-container.modal.button.add')}
      </Button>
    </ContainerRowsWrapper>
  );
};
